import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { formatComponentBody } from '../../components/widgets/PageComponent/PageComponentWidget.logic';
import { PageComponent, PageComponentsMap, PageComponentTagId, PageFlexComponents } from '../../models';
import { fetchPageComponents } from '../../services/issuers/issuers.service';
import { queryKeys } from '../queryKeyFactory';

const FLEX_COMPONENT_TITLE_PREFIX_REGEX = /^flex\s\d* - /gi;

const extractPageComponentTagId = ({ tags }: PageComponent) => tags?.[0]?.tagId;

const isFlexComponent = (PageComponent: PageComponent) => {
  const tagId = extractPageComponentTagId(PageComponent);
  return Object.values<string>(PageFlexComponents).includes(tagId);
};

const transformComponent = (PageComponent: PageComponent): PageComponent => {
  if (isFlexComponent(PageComponent) && FLEX_COMPONENT_TITLE_PREFIX_REGEX.test(PageComponent.title)) {
    return {
      ...PageComponent,
      title: PageComponent.title
        .replace(FLEX_COMPONENT_TITLE_PREFIX_REGEX, '')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim(),
    };
  }
  return PageComponent;
};

const getPageComponentsMap = (PageComponents: PageComponent[]): PageComponentsMap =>
  PageComponents.reduce((acc: PageComponentsMap, value: PageComponent) => {
    const tagId = extractPageComponentTagId(value);
    if (Object.values<string>(PageComponentTagId).includes(tagId)) {
      const formattedBody = formatComponentBody(value);
      // don't include empty body components
      if (formattedBody) {
        acc[tagId] = transformComponent(value);
      }
    }

    return acc;
  }, {});

export default (
  companyTag: string,
  options?: UseQueryOptions<PageComponent[], Error, PageComponentsMap>
): UseQueryResult<PageComponentsMap, Error> => {
  return useQuery<PageComponent[], Error, PageComponentsMap>(
    queryKeys.fetchIssuers.components.all(companyTag),
    () => fetchPageComponents(companyTag),
    { select: (PageComponents) => getPageComponentsMap(PageComponents), ...options }
  );
};
